import * as React from "react";
import { Profile, ProfileLineItem } from "@interfaces/profile";
import { decodeProfile, encodeProfile } from "decoders/profile";
import { getApiData, listApiData, postApiData } from "@hooks/utils/api";

export const useProfile = (organisationId?: string) => {
  const [profiles, setProfiles] = React.useState<ProfileLineItem[]>([]);
  const [isProfileLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const getProfile = async (
    id: string
  ): Promise<ProfileLineItem | undefined> => {
    try {
      setIsLoading(true);
      const profileResult = await getApiData("getProfile", "profile", id);
      if (Object.keys(profileResult.data).length === 0) {
        return undefined;
      }
      return profileResult
        ? decodeProfile(profileResult.data as Profile)
        : undefined;
    } catch (e: any) {
      setError("Could not fetch profile");
    } finally {
      setIsLoading(false);
    }
  };

  const listProfiles = async (organisationId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const profileResult = await listApiData(
        "listProfiles",
        "profile",
        organisationId
      );
      setProfiles(profileResult.data.map((p) => decodeProfile(p as Profile)));
    } catch (e: any) {
      setError("Could not list profiles");
    } finally {
      setIsLoading(false);
    }
  };

  const searchProfiles = async (query: string): Promise<ProfileLineItem[]> => {
    try {
      const profileResult = await listApiData(
        "searchProfiles",
        "profile",
        query
      );
      return profileResult.data.map((p) => decodeProfile(p as Profile));
    } catch (e: any) {
      setError("Could not search profiles");
      return [];
    }
  };

  const createProfile = async (
    profile: ProfileLineItem
  ): Promise<ProfileLineItem> => {
    try {
      setIsLoading(true);
      const encodedProfile = encodeProfile(profile);
      const upsertedProfile = await postApiData(
        "createProfile",
        "profile",
        encodedProfile
      );
      const decodedProfile = decodeProfile(upsertedProfile.data as Profile);
      setProfiles([...profiles, decodedProfile]);
      return decodedProfile;
    } catch (e: any) {
      setError("Could not list profiles");
    } finally {
      setIsLoading(false);
    }
    return profile;
  };

  const updateProfile = async (profile: ProfileLineItem): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedProfile = encodeProfile(profile);
      await postApiData("updateProfile", "profile", encodedProfile);
      setProfiles(
        profiles.map((c) => {
          if (c.id === profile.id) {
            return profile;
          }
          return c;
        })
      );
    } catch (e: any) {
      setError("Could not update profile");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProfile = async (profile: ProfileLineItem): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedProfile = encodeProfile(profile);
      await postApiData("deleteProfile", "profile", encodedProfile);
      setProfiles(profiles.filter((p) => p.id !== profile.id));
    } catch (e: any) {
      setError("Could not delete profile");
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (organisationId) {
      listProfiles(organisationId);
    }
  }, [organisationId]);

  return {
    profiles,
    getProfile,
    listProfiles,
    createProfile,
    updateProfile,
    deleteProfile,
    searchProfiles,
    isProfileLoading,
    error,
  };
};
